"use client";
import Link from "next/link";
import Image from "next/image";
import { 
  FaYoutube, 
  FaFacebookF, 
  FaInstagram, 
  FaLinkedinIn, 
  FaMapMarkerAlt, 
  FaEnvelope, 
  FaPhoneAlt,
  FaPaperPlane
} from "react-icons/fa";
import { IoLogoWhatsapp } from "react-icons/io";
import { useForm } from "react-hook-form";
import React, { useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { prodServerUrl } from "../../app/utils/globals";
import ScrollToTop from "../common/ScrollToTop/ScrollToTop";

export default function Footer() {
  const [disable, setDisable] = useState(false);
  const {
    handleSubmit,
    register,
    reset,
    formState: { errors },
  } = useForm();

  const submitHandler = async ({ email }) => {
    try {
      if (!email) return;
      setDisable(true);
      const { status } = await axios.post(`${prodServerUrl}/subscribe-us`, { email });

      if (status === 200) {
        toast.success("Thank you for subscribing!");
        reset();
      }
    } catch (e) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setDisable(false);
    }
  };

  const quickLinks = [
    { href: "/courses", name: "All Courses" },
    { href: "/career", name: "Careers" },
    { href: "/about", name: "About Us" },
    { href: "/blog", name: "Blog" },
    { href: "/contact", name: "Contact" },
  ];

  const legalLinks = [
    { href: "/terms", name: "Terms & Conditions" },
    { href: "/policy", name: "Privacy Policy" },
    { href: "/refund", name: "Refund Policy" },
  ];

  return (
    <footer className="relative bg-slate-950 text-slate-300 font-sans overflow-hidden">
      
      {/* --- Background Effects --- */}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-600/10 rounded-full blur-[100px] pointer-events-none" />
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-purple-600/10 rounded-full blur-[100px] pointer-events-none" />
      
      {/* --- Main Footer Content --- */}
      <div className="container mx-auto px-6 py-20 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 xl:gap-16">
          
          {/* 1. Brand & Description (4 Columns) */}
          <div className="lg:col-span-4 space-y-6">
            <Link href="/" className="inline-block group">
              <Image 
                src={'/images/vidyastu-logo.png'}
                width={200}
                height={200}
                alt="Vidyastu Logo"
              />
            </Link>
            <p className="text-slate-400 leading-relaxed text-sm pr-4">
              Empowering the next generation of learners with cutting-edge skills in Data Science, Web Development, and Digital Marketing. Join us to reshape your career.
            </p>
            
            {/* App Store Badge */}
            <div className="pt-2">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Download App</p>
              <Link 
                href="https://play.google.com/store/apps/details?id=com.sisyphusinfotech.vidyastu"
                target="_blank"
                className="inline-block transition-transform hover:-translate-y-1"
              >
                <div className="bg-slate-900 border border-slate-800 rounded-xl p-2 pr-4 flex items-center gap-3 hover:border-slate-700 transition-colors">
                  <Image src="/images/google-play-store.svg" width={30} height={30} alt="Play Store" />
                  <div className="flex flex-col">
                    <span className="text-[10px] leading-none text-slate-400">GET IT ON</span>
                    <span className="text-sm font-bold text-white leading-tight">Google Play</span>
                  </div>
                </div>
              </Link>
            </div>
          </div>

          {/* 2. Quick Links (2 Columns) */}
          <div className="lg:col-span-2 space-y-6">
            <h3 className="text-white font-bold text-lg">Company</h3>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.name}>
                  <Link href={link.href} className="text-sm hover:text-blue-400 transition-colors">
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* 3. Legal & Support (2 Columns) */}
          <div className="lg:col-span-2 space-y-6">
            <h3 className="text-white font-bold text-lg">Legal</h3>
            <ul className="space-y-3">
              {legalLinks.map((link) => (
                <li key={link.name}>
                  <Link href={link.href} className="text-sm hover:text-blue-400 transition-colors">
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* 4. Newsletter & Contact (4 Columns) */}
          <div className="lg:col-span-4 space-y-6">
            <h3 className="text-white font-bold text-lg">Stay Updated</h3>
            <p className="text-sm text-slate-400">Subscribe to our newsletter for the latest updates and course offers.</p>
            
            <form onSubmit={handleSubmit(submitHandler)} className="relative max-w-sm">
              <input
                type="email"
                placeholder="Email address"
                className="w-full bg-slate-900/50 border border-slate-800 rounded-lg py-3 pl-4 pr-12 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all"
                {...register("email", { required: true })}
              />
              <button 
                type="submit"
                disabled={disable}
                className="absolute right-1.5 top-1.5 p-2 bg-blue-600 hover:bg-blue-500 text-white rounded-md transition-colors disabled:opacity-50"
              >
                <FaPaperPlane size={12} />
              </button>
            </form>
            {errors.email && <span className="text-xs text-red-500 block mt-1">Please enter a valid email.</span>}

            <div className="pt-4 space-y-3">
              <div className="flex items-start gap-3 text-sm text-slate-400 group">
                <FaMapMarkerAlt className="mt-1 text-blue-500 shrink-0" />
                <span className="group-hover:text-slate-300 transition-colors">
                  74, Rastraguru Ave, Clive House, Dum Dum, Kolkata - 700028
                </span>
              </div>
              <div className="flex items-center gap-3 text-sm text-slate-400 group">
                <FaPhoneAlt className="text-blue-500 shrink-0" />
                <a href="tel:+917450010203" className="group-hover:text-white transition-colors">+91-7450010203</a>
              </div>
              <div className="flex items-center gap-3 text-sm text-slate-400 group">
                <FaPhoneAlt className="text-blue-500 shrink-0" />
                <a href="tel:+917980274926" className="group-hover:text-white transition-colors">+91-7980274926</a>
              </div>
              <div className="flex items-center gap-3 text-sm text-slate-400 group">
                <FaEnvelope className="text-blue-500 shrink-0" />
                <a href="mailto:academic-counsellor@vidyastu.in" className="group-hover:text-white transition-colors">academic-counsellor@vidyastu.in</a>
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* --- Footer Bottom --- */}
      <div className="border-t border-slate-800 bg-black relative z-10">
        <div className="container mx-auto px-6 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            
            {/* Copyright */}
            <div className="text-xs text-center md:text-left">
              <p className="text-white">&copy; {new Date().getFullYear()} Vidyastu Education Pvt. Ltd. All rights reserved.</p>
            </div>

            {/* Social Icons */}
            <div className="flex items-center gap-3">
              {[
                { icon: <FaFacebookF />, href: "https://www.facebook.com/vidyastueducation", color: "hover:bg-[#1877F2]" },
                { icon: <FaYoutube />, href: "https://www.youtube.com/@vidyastu1787", color: "hover:bg-[#FF0000]" },
                { icon: <IoLogoWhatsapp />, href: "https://wa.me/917450010203", color: "hover:bg-[#25D366]" },
                { icon: <FaLinkedinIn />, href: "https://www.linkedin.com/company/vidyastu-education/", color: "hover:bg-[#0A66C2]" },
              ].map((social, idx) => (
                <a
                  key={idx}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`w-8 h-8 flex items-center justify-center rounded-full bg-slate-900 border border-slate-800 text-slate-400 transition-all duration-300 hover:text-white hover:border-transparent ${social.color}`}
                >
                  {social.icon}
                </a>
              ))}
            </div>

            {/* Developer Credit */}
            {/* <div className="text-xs flex items-center gap-1">
              <span>Made by</span>
              <a href="https://amrutglobalsolutions.com/" target="_blank" className="text-slate-400 hover:text-white transition-colors">Amrut Global Solutions Pvt. Ltd.</a>
            </div> */}
          </div>
        </div>
      </div>
      
      {/* Scroll to Top (Positioned Fixed usually, but kept here for structure) */}
      <div className="fixed bottom-6 right-6 z-50">
        <ScrollToTop />
      </div>
      
    </footer>
  );
}